import gql from 'graphql-tag';
import _ from 'underscore';

import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callWithDataModifications} from 'audit/DataModificationsModal';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {setLocationHref} from 'BenchlingUtil';
import requestFetchValidationExtras from 'bioentities/requestFetchValidationExtras';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import {BioentityAliasInfo, UnregisteredBioentity} from 'constants/RegistryConstants';
import FolderDAO from 'file-browser/FolderDAO';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import AppDispatcher from 'lib/Dispatcher';
import notifications from 'notifications';
import {
  GetRegistryOwnerUrlIdQuery,
  GetRegistryOwnerUrlIdQueryVariables,
} from 'registry/__generated/GetRegistryOwnerUrlIdQuery';
import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import {withLoadingScreen} from 'util/Promises';

import * as RegistryDAO from './RegistryDAO';

export const GET_REGISTRY_OWNER_URL_ID_QUERY = gql`
  query GetRegistryOwnerUrlIdQuery($registryId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    registry(id: $registryId) {
      ... on RegistryNode {
        id
        ownerJson
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const RegistryActions = {
  activateRegistry: (registryId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.REGISTRY_ACTIVATED,
      registryId,
    });
  },

  /**
   * Fetch the owner's URL identifier for the given registry.
   *
   * This helps callers construct the URL for the registry or a child object like a dropdown or entity schema.
   * The URL typically requires the owner's URL identifier, the registry's API ID, and the object's API ID to
   * construct.
   *
   * This method assumes the user has read access to the registry.
   */
  async getOwnerUrlId(registryId: string, options: {loadingMessage: string}): Promise<string> {
    const data = await withLoadingScreen(
      doQuerySafe<GetRegistryOwnerUrlIdQuery, GetRegistryOwnerUrlIdQueryVariables>(
        {query: GET_REGISTRY_OWNER_URL_ID_QUERY},
        {variables: {registryId}}
      ),
      options.loadingMessage
    );
    assert(!isInaccessible(data.registry), 'Registry must be accessible.');
    return data.registry.ownerJson.url_identifier;
  },

  unregisterWithDataMods: (
    fileIds: Array<string>,
    folderId: string,
    parentFolderIds: Array<string>
  ): void => {
    callWithDataModifications({
      changeSummary: getInventoryRegistryChangeSummary({
        apiIds: fileIds,
        action: InventoryRegistryDataModAction.UNREGISTER,
      }),
      onPerformAction: (dataModsHeader?: DataModificationsHeader) => {
        RegistryActions.unregister(fileIds, folderId, parentFolderIds, dataModsHeader);
      },
      apiIds: fileIds,
      analyticsParams: {
        module: 'REGISTRY',
        actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
        modifiedAction: InventoryRegistryDataModAction.UNREGISTER,
        numModifiedTotalItems: fileIds.length,
        entryId: null,
      },
      useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY,
      useFolderMoveDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY
        ? {apiIds: fileIds, destinationFolderApiId: folderId}
        : undefined,
    });
  },

  unregister: (
    fileIds: Array<string>,
    folderId: string,
    parentFolderIds: Array<string>,
    dataModsHeader?: DataModificationsHeader
  ): void => {
    LoadingScreen.show();
    RegistryDAO.unregister(fileIds, folderId, dataModsHeader)
      .done((response) => {
        const permissionsByItemId: {[fileId: string]: Permissions} = {};
        for (const file of response.files) {
          permissionsByItemId[file.id] = file.permissions;
        }
        RegistryActions.dispatchForUnregister(response.files, folderId, parentFolderIds, permissionsByItemId);
        for (const fileId of fileIds) {
          requestFetchValidationExtras(fileId);
        }
        notifications.success('Files unregistered');
      })
      .fail(notifications.notifyError)
      .always(() => {
        LoadingScreen.hide();
      });
  },

  dispatchForUnregister: (
    files: Array<UnregisteredBioentity>,
    folderId: string,
    parentFolderIds: Array<string>,
    permissionsById: {[id: string]: Permissions}
  ): void => {
    const fileIds = files.map(({id}) => id);
    AppDispatcher.dispatch({
      actionType: EventConstants.FILE_BROWSER_UNREGISTER_FILES,
      ids: fileIds,
      parentFolderIds,
    });
    const bioentityAliasInfosById: {[fileId: string]: Array<BioentityAliasInfo>} = {};
    for (const file of files) {
      AppDispatcher.dispatch({
        actionType: EventConstants.FILE_BROWSER_RENAME_FILE,
        id: file.id,
        name: file.name,
        displayLabel: file.displayLabel,
      });
      bioentityAliasInfosById[file.id] = file.bioentityAliasInfos;
    }

    AppDispatcher.dispatch({
      actionType: EventConstants.BIOENTITY_ALIAS_INFO_LOAD_COMPLETED,
      bioentityAliasInfosById,
    });
    // eslint-disable-next-line no-restricted-syntax
    const dirtyFolderIds = _.uniq([folderId, ...Array.from(parentFolderIds)]);
    for (const dirtyFolderId of Array.from(dirtyFolderIds)) {
      FolderDAOCache.invalidateFolderContents(dirtyFolderId);
    }
    FolderDAO.fetch(folderId, {summary: '1'}).then((folderResp) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.FILE_BROWSER_MOVE_FILES_COMPLETED,
        ids: fileIds,
        permissionsById,
        folder: folderResp,
      });
    });
  },

  delete: (registryId: string): void => {
    LoadingScreen.show('Deleting...');
    RegistryDAO.deleteRegistry(registryId)
      .done(() => {
        AppDispatcher.dispatch({
          actionType: EventConstants.FILE_BROWSER_SOURCE_DELETED,
          id: registryId,
        });
        // Redirect to the home page
        setLocationHref('/');
      })
      .fail(notifications.notifyError)
      .always(() => {
        LoadingScreen.hide();
      });
  },
};

export default RegistryActions;
